// =====================================================================
//  Modèle de Stratégie OTP (back-office)
//  ---------------------------------------------------------------------
//  Forme ALIGNÉE sur OtpStrategyModel C# (PascalCase, pattern à plat) :
//  le ClientWrapper sert de proxy sans transformation, donc le modèle TS
//  doit être identique au modèle .NET sérialisé.
//
//  Les champs se répartissent en deux familles (cf. reco) :
//   - Paramètres définis dans le BO (cette stratégie).
//   - Paramètres envoyés par l'application appelante à l'exécution
//     (mail, tel, contexte) — NON présents ici, voir « Contrat d'appel ».
// =====================================================================

/** Alphabet utilisé pour générer le code. Aligné C# OtpAlphabetEnum / colonne PATTERN_ALPHABET. */
export enum OtpAlphabet {
    /** 0-9 */
    Numeric = 0,
    /** A-Z 0-9 */
    Alphanumeric = 1,
    /** A-Z 0-9 sans caractères ambigus (O/0, I/1, ...) */
    AlphanumericNoAmbiguous = 2,
}

/** Casse appliquée au code généré. Aligné C# OtpCaseEnum / colonne PATTERN_CASE. */
export enum OtpCase {
    Upper = 0,
    Lower = 1,
    Mixed = 2,
}

/**
 * Canaux d'envoi du code. MASQUE DE BITS (se combine avec OR) — aligné sur
 * la colonne OTP_STRATEGY.CHANNELS (INT) et l'enum C# OtpChannelEnum [Flags].
 * Mail + SMS = 3.
 */
export enum OtpChannel {
    Mail = 1,
    Sms = 2,
}

/**
 * Modèle complet d'une stratégie OTP (éditeur).
 * Champs à PLAT, identiques à OtpStrategyModel C#.
 */
export interface IOtpStrategyModel {
    Primarykey?: string;
    FkProjet?: string;

    // --- Généralités ---
    Nom?: string;
    Libelle?: string;

    // --- Code (pattern) ---
    PatternLength?: number;
    PatternAlphabet?: OtpAlphabet;
    PatternCase?: OtpCase;
    PatternGroupSize?: number;
    PatternGroupSeparator?: string;
    ExpirationMinutes?: number;
    MaxAttempts?: number;

    // --- Message ---
    /** Masque de bits des canaux activés (OtpChannel.Mail | OtpChannel.Sms). */
    Channels?: number;
    FkMailTemplate?: string;
    FkSmsTemplate?: string;

    // --- Lien & retour (NON exposés en UI) ---
    // Le mécanisme de callback/redirection n'est pas implémenté côté runtime
    // (EASYMAIL n'appelle jamais le callback). Ces champs restent dans le
    // contrat pour ne pas casser la sérialisation backend, mais ils n'ont plus
    // d'éditeur back-office. Ils resserviront le jour où EASYMAIL hébergera la
    // page de saisie du code. Voir docs/OTP.md.
    ChallengePageUrl?: string;
    /** Whitelist des callbackUrl autorisés (anti open-redirect). Inactif en UI. */
    CallbackWhitelist?: string[];
}

/** Filtre de lecture (aligné OtpStrategyFilterModel C#). */
export interface IOtpStrategyFilterModel {
    AsNoTracking?: boolean;
    Primarykey?: string;
    FkProjet?: string;
}

/** Aides de manipulation du masque de canaux (bitmask). */
export class OtpChannelMask {
    static Has = (mask: number | undefined, channel: OtpChannel): boolean =>
        ((mask ?? 0) & channel) === channel;

    static Set = (mask: number | undefined, channel: OtpChannel, enabled: boolean): number => {
        const current = mask ?? 0;
        return enabled ? current | channel : current & ~channel;
    };

    static Libelle = (mask: number | undefined): string => {
        const parts: string[] = [];
        if (OtpChannelMask.Has(mask, OtpChannel.Mail)) parts.push("Mail");
        if (OtpChannelMask.Has(mask, OtpChannel.Sms)) parts.push("SMS");
        return parts.length ? parts.join(" + ") : "—";
    };
}
